import { functions } from '@/firebase/config'
import { httpsCallable } from 'firebase/functions'

// Global state for PayPal
let paypalConfigCache = null
let paypalScriptLoading = false
let paypalScriptPromise = null

// Utility function to wait for condition with timeout
const waitForCondition = (condition, timeout = 10000, interval = 100) => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now()
    
    const check = () => {
      if (condition()) {
        resolve()
      } else if (Date.now() - startTime >= timeout) {
        reject(new Error('Timeout waiting for condition'))
      } else {
        setTimeout(check, interval)
      }
    }
    
    check()
  })
}

// Get PayPal config with caching and retry logic
const getPayPalConfig = async (retries = 3) => {
  if (paypalConfigCache) {
    return paypalConfigCache
  }
  
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.log(`Fetching PayPal config (attempt ${attempt}/${retries})`)
      const getConfig = httpsCallable(functions, 'getPayPalConfig')
      const { data } = await getConfig()
      
      if (!data.clientId) {
        throw new Error('Missing clientId in PayPal config')
      }
      
      paypalConfigCache = data
      console.log('PayPal config fetched successfully:', { clientId: data.clientId, mode: data.mode })
      return data
    } catch (error) {
      console.error(`PayPal config fetch attempt ${attempt} failed:`, error)
      
      if (attempt === retries) {
        throw new Error(`Failed to get PayPal config after ${retries} attempts: ${error.message}`)
      }
      
      // Wait before retry with exponential backoff
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000)
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
}

// Load PayPal script with better error handling and deduplication
const loadPayPalScript = async (clientId, retries = 3) => {
  // If script is already loading, wait for it
  if (paypalScriptLoading && paypalScriptPromise) {
    console.log('PayPal script already loading, waiting...')
    return paypalScriptPromise
  }
  
  // If already loaded, return immediately
  if (window.paypal && window.paypal.Buttons) {
    console.log('PayPal SDK already available')
    return Promise.resolve()
  }
  
  // Remove any existing scripts to avoid conflicts
  const existingScripts = document.querySelectorAll('script[src*="paypal.com/sdk"]')
  existingScripts.forEach(script => script.remove())
  
  paypalScriptLoading = true
  
  paypalScriptPromise = new Promise((resolve, reject) => {
    let attempt = 0
    
    const attemptLoad = () => {
      attempt++
      console.log(`Loading PayPal script (attempt ${attempt}/${retries})`)
      
      const script = document.createElement('script')
      const scriptUrl = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=GBP&intent=capture&disable-funding=venmo,paylater`
      script.src = scriptUrl
      script.async = true
      script.defer = true
      
      const cleanup = () => {
        script.removeEventListener('load', onLoad)
        script.removeEventListener('error', onError)
      }
      
      const onLoad = async () => {
        cleanup()
        
        try {
          // Wait for PayPal to be fully available
          await waitForCondition(() => window.paypal && window.paypal.Buttons, 5000)
          console.log('PayPal SDK loaded and ready')
          paypalScriptLoading = false
          resolve()
        } catch (error) {
          console.error('PayPal SDK loaded but not ready:', error)
          if (attempt < retries) {
            setTimeout(attemptLoad, 1000 * attempt)
          } else {
            paypalScriptLoading = false
            reject(new Error('PayPal SDK loaded but not ready'))
          }
        }
      }
      
      const onError = (error) => {
        cleanup()
        console.error(`PayPal script load attempt ${attempt} failed:`, error)
        
        if (attempt < retries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 3000)
          setTimeout(attemptLoad, delay)
        } else {
          paypalScriptLoading = false
          reject(new Error(`Failed to load PayPal SDK after ${retries} attempts`))
        }
      }
      
      script.addEventListener('load', onLoad)
      script.addEventListener('error', onError)
      
      // Set a timeout for the script load
      setTimeout(() => {
        if (paypalScriptLoading) {
          onError(new Error('Script load timeout'))
        }
      }, 15000)
      
      document.head.appendChild(script)
    }
    
    attemptLoad()
  })
  
  return paypalScriptPromise
}

export const initPayPalButton = async (amount, onSuccess, description = '', quantity = 1) => {
  try {
    // Validate inputs
    if (!amount || amount <= 0) {
      throw new Error('Invalid amount for PayPal payment')
    }
    
    if (!quantity || quantity <= 0) {
      throw new Error('Invalid quantity for PayPal payment')
    }
    
    if (typeof onSuccess !== 'function') {
      throw new Error('onSuccess callback must be a function')
    }
    
    console.log('PayPal initialization details:', {
      amount,
      description,
      quantity,
      windowPaypal: !!window.paypal
    })

    // Get PayPal config
    const { clientId, mode } = await getPayPalConfig()
    
    // Load PayPal script if needed
    await loadPayPalScript(clientId)
    
    console.log('Creating PayPal button with validated parameters')
    return createButton(amount, onSuccess, description, quantity)
  } catch (error) {
    console.error('Error initializing PayPal:', error)
    throw new Error(`PayPal initialization failed: ${error.message}`)
  }
}

const createButton = (amount, onSuccess, description, quantity) => {
  if (!window.paypal || !window.paypal.Buttons) {
    throw new Error('PayPal SDK not available')
  }
  
  const unitPrice = (amount / quantity).toFixed(2)
  const totalAmount = amount.toFixed(2)
  
  return window.paypal.Buttons({
    style: {
      layout: 'vertical',
      color: 'gold',
      shape: 'rect',
      label: 'pay',
      height: 40
    },
    createOrder: (data, actions) => {
      console.log('Creating PayPal order:', {
        amount: totalAmount,
        unitPrice,
        quantity,
        description
      })
      
      try {
        return actions.order.create({
          purchase_units: [{
            amount: {
              currency_code: 'GBP',
              value: totalAmount,
              breakdown: {
                item_total: {
                  currency_code: 'GBP',
                  value: totalAmount
                }
              }
            },
            description: description,
            items: [
              {
                name: description,
                unit_amount: {
                  currency_code: 'GBP',
                  value: unitPrice
                },
                quantity: quantity.toString(),
                description: `Ticket for ${description}`,
                category: 'DIGITAL_GOODS'
              }
            ]
          }],
          intent: 'CAPTURE'
        })
      } catch (error) {
        console.error('Error creating PayPal order:', error)
        throw error
      }
    },
    onApprove: async (data, actions) => {
      console.log('PayPal payment approved, capturing order:', data.orderID)
      
      try {
        const order = await actions.order.capture()
        console.log('PayPal order captured successfully:', order.id)
        await onSuccess(order)
      } catch (error) {
        console.error('Error capturing PayPal order:', error)
        throw error
      }
    },
    onError: (error) => {
      console.error('PayPal button error:', error)
      // You might want to show a user-friendly error message here
    },
    onCancel: (data) => {
      console.log('PayPal payment cancelled:', data)
      // You might want to track this cancellation
    }
  })
}

// Utility function to clear PayPal cache (useful for testing or config changes)
export const clearPayPalCache = () => {
  paypalConfigCache = null
  paypalScriptLoading = false
  paypalScriptPromise = null
  console.log('PayPal cache cleared')
}